import useData from "@/hooks/useData";
import useQueries from "@/hooks/useQueries";
import MainLayout from "@/shared/components/MainLayout";
import Header from "@/shared/components/ui/Header";
import { API_URL } from "@/shared/utils/API_URL";
import { buildQuery } from "@/shared/utils/buildQuery";
import { PackageOpen, PackageSearch, Search, Zap } from "lucide-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";


export default function SearchByItem() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [searchParams] = useSearchParams();
    const accountStatement = searchParams.get('account_statement');


    const [itemValue, setItemValue] = useState('');
    const [searchValue, setSearchValue] = useState('');


    const storedOptions = localStorage.getItem('temp_options');
    const options = storedOptions ? JSON.parse(storedOptions) : [];
    const { option_value: parent_id } = options?.find(opt => opt.option_name === "parent_id") || {};


    const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setSearchValue(itemValue);
    }

    const queries = useQueries();
    const q = queries ? buildQuery(queries?.searchAccountQuery, { parent_id: parent_id || "" }) : '';

    const { fetchedData: data } = useData({
        apiUrl: API_URL,
        q,
        t: "sel",
        dependency: [q],
    });

    const accounts = data?.data?.result || [];

    const normalizeArabic = (str: string) => {
        if (!str) return "";
        return str
            .replace(/[أإآ]/g, "ا")
            .replace(/ة/g, "ه")
            .replace(/ى/g, "ي")
            .replace(/\s+/g, " ")
            .trim()
            .toLowerCase();
    };

    const searchWords = normalizeArabic(searchValue).split(" ").filter(Boolean);

    const filteredAccounts = accounts
        // ?.filter((item: any) => item?.item_type !== 'c_cont')
        ?.filter((item: any) => {
            if (!searchWords.length) return false;
            const nameNorm = normalizeArabic(item?.account_name);
            return searchWords.every(word => nameNorm.includes(word));
        });

    return (
        <MainLayout>
            <Header title="Search Account" info={q} />
            <div className="mx-auto max-w-4xl px-4 py-8">

                <div className="mb-5">
                    <form onSubmit={handleSearch} className="relative group">
                        <div className="absolute inset-y-0 start-0 flex items-center ps-4 pointer-events-none">
                            <PackageSearch size={22} className="text-violet-500 transition-transform duration-300 group-focus-within:scale-110" />
                        </div>
                        <input
                            type="text"
                            className="w-full bg-white border-none ring-2 ring-slate-100 focus:ring-4 focus:ring-violet-500/20 rounded-[1rem] ps-11 pe-10 py-4 text-slate-800 font-bold shadow-xl shadow-slate-200/50 placeholder:text-slate-300 transition-all outline-none text-lg"
                            placeholder={t("Search account...")}
                            required
                            value={itemValue}
                            onChange={(e) => setItemValue(e.target.value)}
                        />
                        <button
                            type="submit"
                            className="absolute end-2.5 top-2.5 bottom-2.5 bg-violet-600 hover:bg-violet-700 text-white px-4 rounded-2xl shadow-lg shadow-violet-200 transition-all active:scale-95 flex items-center justify-center"
                        >
                            <Search size={20} strokeWidth={3} />
                        </button>
                    </form>
                </div>

                <div className="flex flex-col items-center justify-center">
                    {searchValue !== '' && filteredAccounts?.length === 0 && (
                        <div className="w-full text-center bg-white p-10 rounded-[1rem] shadow-2xl shadow-slate-200/50 border border-slate-50 max-w-sm">
                            <div className="w-20 h-20 bg-rose-50 rounded-[1rem] flex items-center justify-center mx-auto mb-6">
                                <PackageOpen size={40} className="text-rose-500" />
                            </div>
                            <h3 className="text-xl font-black text-slate-800 mb-2">{t("Account Not Found")}</h3>

                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {filteredAccounts.map((account: any) => (
                        <div
                            key={account?.account_id}
                            className="group relative bg-white rounded-2xl p-5 shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300 border border-slate-100 flex flex-col justify-between"
                        >
                            <div>
                                <h3
                                    onClick={() => {
                                        if (!accountStatement) return;
                                        void navigate(`/account_statement/${account?.account_id}/${encodeURIComponent(account?.account_name)}`)
                                    }}
                                    className={`${accountStatement && "cursor-pointer"} text-lg font-bold text-slate-800 leading-snug group-hover:text-violet-600 transition-colors line-clamp-2`}>
                                    {account?.account_name}
                                </h3>

                                <div className="flex items-baseline gap-1">
                                    <span className="text-2xl font-black text-slate-900">
                                        {account?.total_balance}
                                    </span>
                                </div>
                            </div>
                            {!accountStatement && (
                                <Link
                                    to={`/add_payment/${account?.account_id}/${encodeURIComponent(account?.account_name)}/${account?.total_balance}`}
                                    className="w-full bg-slate-50 group-hover:bg-violet-600 text-slate-600 group-hover:text-white py-3 rounded-xl font-bold text-center transition-all duration-300 flex items-center justify-center gap-2"
                                >
                                    <Zap size={18} />
                                    {t("Add Payment")}
                                </Link>
                            )}

                        </div>
                    ))}
                </div>
            </div>
        </MainLayout>
    );
}